<template>
  <div class="open-app-page">
    <main class="open-app-card">
      <h1 class="title">{{ c.heading }}</h1>
      <p class="body">{{ c.body }}</p>
      <a class="btn" :href="downloadUrl">{{ c.downloadCta }}</a>
    </main>
  </div>
</template>

<script setup>
import { computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { openAppContent } from "@/i18n/emailPreferences";

/**
 * Deep-link hand-off for links inside emails.
 *
 * Email clients strip or refuse custom URL schemes, so campaign CTAs point at
 * this https route instead. It attempts the app scheme and falls back to the
 * download page if the app isn't installed (no navigation happened).
 */
const route = useRoute();

const lang = computed(() => (route.query.lang === "en" ? "en" : "bg"));
const c = computed(() => openAppContent[lang.value]);

// Allow-list the views a link may target — the value arrives from a URL, and
// interpolating it straight into a scheme URL would let a crafted link point
// the app anywhere.
const VIEWS = { chat: "chat", topics: "topics", resume: "resume" };
const view = computed(() => VIEWS[route.query.view] || "chat");

const downloadUrl = computed(
  () => `https://momfident.io/download?lang=${lang.value}`
);

onMounted(() => {
  document.title = c.value.metaTitle;

  const appUrl = `momfident://open?view=${view.value}`;
  const fallback = setTimeout(() => {
    window.location.replace(downloadUrl.value);
  }, 1500);

  // If the app opens, the page is backgrounded and the fallback is cancelled.
  const cancel = () => {
    if (document.hidden) clearTimeout(fallback);
  };
  document.addEventListener("visibilitychange", cancel);

  window.location.href = appUrl;
});
</script>

<style scoped lang="scss">
@import "~@/assets/scss/variables.scss";

.open-app-page {
  font-family: $font-regular;
  background: #fbe9ec;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.open-app-card {
  max-width: 480px;
  text-align: center;
}

.title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 12px;
}

.body {
  font-size: 16px;
  line-height: 1.55;
  margin-bottom: 24px;
}

.btn {
  display: inline-block;
  background: #6b6b9c;
  color: #ffffff;
  border-radius: 50px;
  padding: 14px 28px;
  font-size: 16px;
  text-decoration: none;
}
</style>
